import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

interface POSColors {
  bg: string;
  surface: string;
  surfaceLight: string;
  border: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accent: string;
  accentLight: string;
}

interface DiscountInputProps {
  subtotal: number;
  discount: number;
  onDiscountChange: (amount: number) => void;
  formatPrice: (price: number) => string;
  colors: POSColors;
  disabled?: boolean;
}

export const DiscountInput: React.FC<DiscountInputProps> = ({
  subtotal,
  discount,
  onDiscountChange,
  formatPrice,
  colors,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState<string>(discount > 0 ? String(discount) : '');
  const [error, setError] = useState<string | null>(null);

  // Sync external discount changes
  useEffect(() => {
    setInputValue(discount > 0 ? String(discount) : '');
    setError(null);
  }, [discount]);

  const validateAndSubmit = (value: string) => {
    const numValue = parseFloat(value) || 0;

    // Handle negative values
    if (numValue < 0) {
      onDiscountChange(0);
      setInputValue('');
      setError(null);
      return;
    }

    // Validate against subtotal
    if (numValue > subtotal) {
      setError(`Cannot exceed ${formatPrice(subtotal)}`);
      onDiscountChange(subtotal);
      setInputValue(String(subtotal));
      return;
    }

    setError(null);
    onDiscountChange(numValue);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    // Show error while typing if exceeds subtotal
    const numValue = parseFloat(value) || 0;
    if (numValue > subtotal) {
      setError(`Cannot exceed ${formatPrice(subtotal)}`);
    } else {
      setError(null);
    }
  };

  const handleBlur = () => {
    validateAndSubmit(inputValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      validateAndSubmit(inputValue);
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleClear = () => {
    setInputValue('');
    setError(null);
    onDiscountChange(0);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <LocalOfferIcon sx={{ fontSize: 16, color: colors.textMuted }} />
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: colors.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Discount
        </Typography>
      </Box>
      <TextField
        type="number"
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder="0"
        disabled={disabled}
        error={!!error}
        helperText={error}
        size="small"
        fullWidth
        inputProps={{
          min: 0,
          max: subtotal,
          'aria-label': 'Discount amount',
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ color: colors.textMuted, fontSize: '14px' }}>
                  DA
                </Typography>
                {discount > 0 && !disabled && (
                  <IconButton
                    size="small"
                    onClick={handleClear}
                    aria-label="Clear discount"
                    sx={{
                      color: colors.textMuted,
                      '&:hover': {
                        color: colors.textPrimary,
                        backgroundColor: alpha(colors.textMuted, 0.1),
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: colors.surfaceLight,
            '& fieldset': {
              borderColor: colors.border,
            },
            '&:hover fieldset': {
              borderColor: colors.textMuted,
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.accent,
            },
            '&.Mui-error fieldset': {
              borderColor: '#EF4444',
            },
          },
          '& .MuiOutlinedInput-input': {
            color: colors.textPrimary,
            '&::placeholder': {
              color: colors.textMuted,
              opacity: 1,
            },
          },
          '& .MuiFormHelperText-root': {
            color: '#EF4444',
            marginLeft: 0,
          },
          // Hide number input spinners
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
          '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
        }}
      />
    </Box>
  );
};

export default DiscountInput;
